/* eslint-disable @typescript-eslint/naming-convention */
import { DnParts, DType, DTypeRepository, DTypeStatus, DTypeToGoLiveRequest, DTypeToGoLiveSchedule, FlushDTypeReply, JsonReply, NextLocator, Partaker } from '@grupo-constant/next-dtype/types';
import { CompanyUser } from '../user/companyUser.model';
import { FormGroup } from '@angular/forms';

export interface DTypeMigrationItem {
    uri?: string;
    name?: string;
    ownerName?: string;
    type?: 'Master' | 'Form';
    masterType?: string;
    dependOf?: string[];
}

export interface DTypeMigrationDestinyItem {
    marca_code?: string;
    cliente_code?: string;
    projectId?: string;
    serviceId?: string;
    type?: 'Brand' | 'Customer' | 'Project' | 'Service';
    name?: string;
    fullRoute?: string;
}

export interface RetrieveMasterFormHierarchyReply extends JsonReply {
    data?: DTypeMigrationItem[];
    masterError?: boolean;
}

export interface FlushMasterFormHierarchyBody {
    locator?: NextLocator;
    items?: DTypeMigrationItem[];
    notCloneMasterData?: boolean;
}
export interface FlushMasterFormHierarchyReply extends JsonReply {
    masterError?: boolean;
}

export interface ImportDTypeMigrationItemBody {
    locator?: NextLocator;
    base64?: string;
}

export interface RetrieveMasterFormDestinyReply extends JsonReply {
    data?: DTypeMigrationDestinyItem[];
}

export interface SqlTableComparison {
    dn?: SqlItemComparison[];
    tableType?: SqlItemComparison;
    elementType?: SqlItemComparison;
    tableName?: SqlItemComparison;
    tableSchema?: SqlItemComparison;
    uriName?: SqlItemComparison;
    linkField?: SqlItemComparison;
    linkType?: SqlItemComparison;
    columns?: SqlItemComparison[];
    indexes?: SqlItemComparison[];
    status?: DTypeStatus;
}

export interface SqlItemComparison {
    name: string;
    value: string;
    tooltipItem?: TooltipItem;
    isDifferent: boolean;
    onlyHere: boolean;
    listValues?: string[];
}

export interface CheckDTypeGoingLiveBody {
    locator: NextLocator;
    facadeUri: string;
    dtype: DType;
}

export interface FlushDTypeRepositoryReply extends FlushDTypeReply {
    equalSQLTableModel?: boolean;
    equalDType?: boolean;
    dtype: DType;
    repository?: DTypeRepository;
    isNewRelease: boolean;
    release?: number;
    partaker: Partaker;
}

export interface QuarantineRequestToGoLive {
    dtypeToGoLiveRequest?: DTypeToGoLiveRequest;
    tableComparision?: QuarantineTableObject[];
    partaker?: Partaker;
}

export interface QuarantineTableObject {
    uri?: string;
    whyThisTable: QuarantineTableWhyThisTable;
    design?: QuarantineTableInfo;
    live?: QuarantineTableInfo;
    bestCompatible?: QuarantineTableInfo;
    result?: QuarantineTableComparisonResult;
    columnComparision?: QuarantineColumnComparison[];
}
export interface QuarantineColumnComparison {
    fieldName?: string;
    result?: QuarantineColumnComparisonResult;
}

export interface QuarantineTableComparison {
    title?: string;
    dn?: QuarantineItemComparison[];
    tableType?: QuarantineItemComparison;
    elementType?: QuarantineItemComparison;
    tableName?: QuarantineItemComparison;
    tableSchema?: QuarantineItemComparison;
    uriName?: QuarantineItemComparison;
    linkField?: QuarantineItemComparison;
    linkType?: QuarantineItemComparison;
    columns?: QuarantineItemComparison[];
    indexes?: QuarantineItemComparison[];
    status?: QuarantineStatus;
}

export interface QuarantineItemComparison {
    name: string;
    value: string;
    tooltipItem?: TooltipItem;
    isDifferent: boolean;
    onlyHere: boolean;
    listValues?: string[];
}

export interface QuarantineTableInfo {
    name?: string;
    schema?: string;
    columns?: QuarantineTableColumn[];
    indexes?: QuarantineTableIndex[];
    derived?: QuarantineTableDerived[];
    whyThisTable?: QuarantineTableWhyThisTable;
}
export interface QuarantineTableColumn {
    name?: string;
    ordinal?: number;
    sqlType?: string;
    type?: string;
    length?: number;
    scale?: number;
    nullable?: boolean;
    charset?: string;
    backgroundType?: string;
    tooltip?: string;
}
export interface QuarantineTableIndex {
    primary?: boolean;
    unique?: boolean;
    cluster?: boolean;
    columns?: QuarantineTableIndexColumn[];
}
export interface QuarantineTableIndexColumn {
    columnName?: string;
    descending?: boolean;
    ordinalPosition?: number;
}
export interface QuarantineTableDerived {
    [key: string]: QuarantineTableColumn;
}
export interface QuarantineTableWhyThisTable {
    type?: string;
    facadeUri?: string;
    uri?: string;
    uriName?: string;
    dn?: DnParts;
    field?: string[];
    complexName: string;
    complexUri: string;
}
export interface TooltipItem {
    tooltip: string;
    show: boolean;
}

export interface QuarantineDrawModel {
    design?: QuarantineTableComparison;
    live?: QuarantineTableComparison;
    bestCompatible?: QuarantineTableComparison;
    tableComparison?: QuarantineTableComparisonResult;
    buttons?: DTypeGoLiveActionButton;
    title?: string;
}

export interface ToggleDTypeLockBody {
    uri: string;
}

export interface ToggleDTypeHiddenBody {
    uri: string;
}

export interface DTypeAlerts {
    showEvened?: boolean;
    showRequestToGoLive?: boolean;
    showScheduleToGoLive?: boolean;
    showMigrating?: boolean;
    showHasRequestToGoLive?: boolean;
}

export interface ForwardDTypeToLiveBody {
    uri: string;
}

export interface ResetDTypeBody {
    uri: string;
}

export interface DTypeToGoLiveScheduleBody {
    uri: string;
    dtypeToGoLiveSchedule?: DTypeToGoLiveSchedule;
    rejected?: boolean;
}

export interface DTypeGoLiveActionButton {
    uri: string;
    message: string;
    design: DTypeGoLiveActionButtonContent;
    compatible: DTypeGoLiveActionButtonContent;
    migrate: DTypeGoLiveActionButtonContent;
}
export interface DTypeGoLiveActionButtonContent {
    show: boolean;
    selected: boolean;
    caption: string;
    info?: string;
}

export interface ChangeDTypeRepositoryNameBody {
    uri: string;
    name: string;
}

export enum QuarantineTableComparisonResult {
    EQUAL = 'EQUAL',
    MISSING_LIVE = 'MISSING_LIVE',
    MISSING_DESIGN = 'MISSING_DESIGN',
    FORWARD_COMPATIBLE = 'FORWARD_COMPATIBLE',
    COMPATIBLE = 'COMPATIBLE',
    INCOMPATIBLE = 'INCOMPATIBLE'
}
export enum QuarantineColumnComparisonResult {
    EQUAL = 'EQUAL',
    MISSING_LIVE = 'MISSING_LIVE',
    MISSING_DESIGN = 'MISSING_DESIGN',
    FORWARD_COMPATIBLE = 'FORWARD_COMPATIBLE',
    BACKWARD_COMPATIBLE = 'BACKWARD_COMPATIBLE',
    INCOMPATIBLE = 'INCOMPATIBLE'
}
export enum QuarantineStatus {
    DESIGN = 'DESIGN',
    LIVE = 'LIVE',
    BEST_COMPATIBLE = 'BEST_COMPATIBLE'
}
export interface RetrieveDTypeDataCollectionBody {
    uri: string;
    columnsForList: boolean;
    chunksize?: number;
    lastConsumedOID?: string;
    count: boolean;
	orderBy?: string;
    discardDeprecated?: boolean;
    deprecatedDate?: string;
}
export interface RetrieveDTypeDataCollectionReply extends JsonReply {
    uri: string;
    size: number;
    data: any[];
    count?: number;
    eof: boolean;
    extraData?: {
        locationChangeRequest: string[]; // location master oid list
    }
}

export interface SearchDTypeDataCollection {
    uri: string;
    expr: string;
    attributeFilters?: any;
    blind?: boolean;
    reload?: boolean;
    chunksize?: number;
	lastConsumedOID?: string;
	count?: boolean;
    discardDeprecated?: boolean;
    searchDate?: string;
}

export interface DownloadGoalModelBody {
    serviceId: string;
}

export interface UploadGoalExcelBody {
    excelType: string;
    locator: NextLocator;
    async: boolean;
    file: string;
}

export interface SearchCompanyUserMissionUserListDNRequest {
    expr?: string;
    locator: NextLocator;
    activeOnly?: boolean;
    discardDeprecated?: boolean;
    onlyActiveServices?: boolean;
    usersWithTasks?: boolean;
}

export interface SearchCompanyUserMissionUserListDNReply extends JsonReply {
    size: number;
	companyUser: CompanyUser[];
	orderedBy: string;
	partaker: Partaker;
}

export interface RetrieveCompanyUserMissionUserListReply extends JsonReply {
    companyUser: CompanyUser[];
    size: number;
}

export interface FlushMasterOrderRequest {
    uri: string;
    oids: string[];
}
